import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCoins, FaCalendarAlt, FaHourglassHalf } from 'react-icons/fa';

import { formaterMois, formaterMontant } from './formatAvances';

/**
 * Les trois chiffres que le salarié cherche en premier, dans cet ordre :
 * combien il doit encore, ce qui partira de son prochain bulletin, et combien
 * de demandes attendent une décision.
 *
 * De l'agrégation d'affichage seulement : chaque chiffre vient de l'échéancier
 * que la Paie a rendu dans `contenu.remboursement`, l'écran ne calcule aucune
 * retenue.
 */
export function calculerSynthese(demandes) {
  const suivies = demandes.filter((demande) => {
    const reste = demande.contenu?.remboursement?.reste_du;
    return typeof reste === 'number' && Number.isFinite(reste);
  });
  const reste = suivies.length
    ? suivies.reduce((total, demande) => total + demande.contenu.remboursement.reste_du, 0)
    : null;

  const aVenir = demandes.flatMap((demande) =>
    (demande.contenu?.remboursement?.echeances || []).filter(
      (echeance) => echeance.statut !== 'prelevee'
    )
  );
  let prochain = null;
  if (aVenir.length) {
    const mois = aVenir.map((echeance) => echeance.mois).sort()[0];
    prochain = {
      mois,
      montant: aVenir
        .filter((echeance) => echeance.mois === mois)
        .reduce((total, echeance) => total + echeance.montant, 0),
    };
  }

  const attente = demandes.filter((demande) => demande.etat === 'en_attente').length;
  return { reste, prochain, attente };
}

/**
 * Un indicateur du bandeau. Jamais tronqué : un montant coupé en « 160 000 … »
 * ne dit plus rien (recette du 21/09/2026). Chiffres tabulaires, pour que les
 * montants s'alignent d'un indicateur à l'autre.
 */
function Indicateur({ icone: Icone, libelle, valeur, detail, testId }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4">
      <span className="w-9 h-9 rounded-lg bg-slate-100 text-[--color-primary] flex items-center justify-center flex-shrink-0">
        <Icone className="w-4 h-4" aria-hidden="true" />
      </span>
      <div className="min-w-0" data-testid={testId}>
        <p className="text-xs text-slate-500">{libelle}</p>
        <p className="text-lg font-semibold text-slate-900 tabular-nums break-words">{valeur}</p>
        {detail && <p className="text-xs text-slate-500 capitalize">{detail}</p>}
      </div>
    </div>
  );
}

// Les libellés du salarié par défaut ; la supervision RH passe les siens —
// « à décider » plutôt que « en attente de décision », l'encours de la société
// plutôt que ce qu'il reste à rembourser.
const LIBELLES_SALARIE = {
  reste: 'mesAvances.synthese.reste',
  prochain: 'mesAvances.synthese.prochain',
  attente: 'mesAvances.synthese.attente',
};

function SyntheseAvances({ demandes, locale, libelles = LIBELLES_SALARIE, prefixe = 'mes-avances' }) {
  const { t } = useTranslation();
  const { reste, prochain, attente } = calculerSynthese(demandes);

  // Un seul bandeau, divisé par des filets, plutôt que trois cartes flottantes :
  // trois chiffres qui se lisent ensemble.
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
      <Indicateur
        icone={FaCoins}
        libelle={t(libelles.reste)}
        valeur={formaterMontant(reste, locale)}
        testId={`${prefixe}-synthese-reste`}
      />
      <Indicateur
        icone={FaCalendarAlt}
        libelle={t(libelles.prochain)}
        valeur={prochain ? formaterMontant(prochain.montant, locale) : '—'}
        detail={prochain ? formaterMois(prochain.mois, locale) : null}
        testId={`${prefixe}-synthese-prochain`}
      />
      <Indicateur
        icone={FaHourglassHalf}
        libelle={t(libelles.attente)}
        valeur={String(attente)}
        testId={`${prefixe}-synthese-attente`}
      />
    </div>
  );
}

export default SyntheseAvances;
